// A workspace's trash (spec §16.4): deleted boards wait here for 30 days,
// restorable at the same address, before they're deleted for good.
import { TRASH_DAYS, type TrashedBoard } from '../../shared/api.ts';
import { ApiError, del, get, post } from '../api.ts';
import { h } from '../util/dom.ts';
import { busy, formError, getMe, header, longDate, mount, relativeTime, rememberWorkspace, setTitle } from './page.ts';

export async function trashPage(slug: string): Promise<void> {
  const me = await getMe();
  const ws = me.workspaces.find((w) => w.slug === slug);
  if (!ws || ws.role === 'guest') throw new ApiError(404, 'Workspace not found.');
  rememberWorkspace(ws.slug);
  setTitle(`Trash · ${ws.name}`);

  const error = formError();
  const body = h('div');
  mount(header(me, ws, 'boards'), h('main', { class: 'page narrow' }, [
    h('p', {}, [h('a', { href: `/w/${ws.slug}` }, ['← Boards'])]),
    h('div', { class: 'page-head' }, [h('h1', {}, ['Trash'])]),
    h('p', { class: 'muted' }, [`Deleted boards stay here for ${TRASH_DAYS} days. Restoring one brings it back with its history, at the same address.`]),
    error, body,
  ]));

  const act = (work: () => Promise<unknown>) => {
    error.clear();
    return work().then(render, (err: unknown) => error.show(err));
  };

  async function render(): Promise<void> {
    const boards = await get<TrashedBoard[]>(`/api/workspaces/${ws!.id}/trash`);
    if (!boards.length) {
      body.replaceChildren(h('div', { class: 'empty' }, [h('p', {}, ['The trash is empty.'])]));
      return;
    }
    body.replaceChildren(h('table', { class: 'table trash-table' }, [h('tbody', {}, boards.map(row))]));
  }

  function row(b: TrashedBoard): HTMLElement {
    const restore = h('button', { type: 'button', class: 'btn small' }, ['Restore']);
    restore.addEventListener('click', () => void busy(restore, () => act(() => post(`/api/boards/${b.id}/restore`))));
    const remove = h('button', { type: 'button', class: 'btn small subtle danger-text' }, ['Delete forever']);
    remove.addEventListener('click', () => {
      if (!confirm(`Delete "${b.title}" for good? This can't be undone.`)) return;
      void busy(remove, () => act(() => del(`/api/boards/${b.id}/permanent`)));
    });
    return h('tr', {}, [
      h('td', {}, [h('strong', {}, [b.title]), h('div', { class: 'muted' }, [`Deleted ${relativeTime(b.deletedAt)}${b.deletedBy ? ` by ${b.deletedBy}` : ''}`])]),
      h('td', { class: 'muted' }, [`Goes for good on ${longDate(b.purgeAt)}`]),
      h('td', { class: 'actions' }, [restore, remove]),
    ]);
  }

  await render();
}
